import { useCallback, useEffect, useRef, useState } from "react";

/**
 * @typedef {Object} SaveObject An object containing exposed handlers and save state.
 * @property {Object} error The error object thrown during the save function execution.
 * @property {function} handleBlur The handler to pass to element onBlur events. Sets isFocused to false.
 * @property {function} handleFocus The handler to pass to element onFocus events. Sets isFocused to true.
 * @property {boolean} isSaving Whether the save function is currently running or not.
 */

/**
 * A hook to automatically run a save function when differences are detected between an initial state and the current monitored state.
 * @param {*} stateToTrack State to track.
 * @param {function} saveFunction Async save function to run when changes are detected must return a promise. IMPORTANT: this must throw on errors in order for the state to detect unsuccessful executions.
 * @param {Object} options
 * @param {function} options.onError Callback to run if an uncaught error occurs while running the save function.
 * @param {function} options.onSaved Callback to run after saving.
 * @param {boolean} options.canSave An override to set whether save should run if changes are detected.
 * @returns {SaveObject} An object containing exposed handlers and save state.
 */
const useAutoSave = (
  stateToTrack,
  saveFunction,
  { canSave, isReady = true, onError, onSaved, resetDependency } = {}
) => {
  const [error, setError] = useState(false);
  const [initialState, setInitialState] = useState();
  const [isFocused, setIsFocused] = useState(false);
  const [isInitialised, setIsInitialised] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const lastFailedStateRef = useRef(null);
  const queuedButtonRef = useRef();
  const resetDependencyRef = useRef();

  const handleBlur = e => {
    setIsFocused(false);

    if (e?.relatedTarget?.type === "button") {
      // As the blur event does not bubble up, if the user tries to click a button while focused on an input field, a blur will occur instead, leaving the button unclicked.
      // To solve this, we can set the related target as a ref, run the save function after a blur, then the button click handler.
      queuedButtonRef.current = e.relatedTarget;
    }
  };

  const handleFocus = () => setIsFocused(true);

  useEffect(() => {
    if (!resetDependency || resetDependency !== resetDependencyRef.current) {
      setIsInitialised(true);
      resetDependencyRef.current = resetDependency;
    }
  }, [resetDependency]);

  useEffect(() => {
    if (
      isReady && // We've stopped fetching initial data.
      isInitialised && // We haven't yet set it in state.
      ((Array.isArray(stateToTrack) && stateToTrack.length) || // Either non-empty array.
        (typeof stateToTrack === "object" && Object.values(stateToTrack).filter(Boolean).length) || // Or non-empty object.
        (typeof stateToTrack !== "object" && stateToTrack != null)) // Or non-nullish primitive.
    ) {
      setInitialState(JSON.parse(JSON.stringify(stateToTrack)));
      setIsInitialised(false);
    }
  }, [isInitialised, isReady, stateToTrack]);

  const executeSave = useCallback(
    async state => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      try {
        await saveFunction(state);
        lastFailedStateRef.current = null;
        setInitialState(JSON.parse(JSON.stringify(state)));

        if (queuedButtonRef.current) {
          queuedButtonRef.current.click();
          queuedButtonRef.current = null;
        }

        setError();
      } catch (error) {
        lastFailedStateRef.current = JSON.stringify(state);
        setError(error);
        if (onError) onError(error);
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
        if (onSaved) onSaved();
      }
    },
    [onError, onSaved, saveFunction]
  );

  useEffect(() => {
    const serialised = JSON.stringify(stateToTrack);

    if (
      isReady &&
      !isFocused &&
      !isInitialised &&
      !isSavingRef.current &&
      initialState &&
      stateToTrack &&
      Boolean(canSave) &&
      serialised !== JSON.stringify(initialState) &&
      serialised !== lastFailedStateRef.current
    ) {
      executeSave(stateToTrack);
    }
  }, [canSave, executeSave, initialState, isFocused, isInitialised, isReady, stateToTrack]);

  return { error, handleBlur, handleFocus, isSaving };
};

export { useAutoSave };
